import { getAccessTokenCookie } from "./cookies";
import { refreshTokenIfNeeded } from "./tokenManager";

/**
 * Bearer token for manual (non-generated-client) API calls — the transcription
 * upload (`uploadWithProgress`) and the legacy `callApi` helper.
 *
 * Reads the same unified in-memory token store the generated `@hey-api` client
 * uses (`getAccessTokenCookie`), so it authenticates BOTH sign-in types:
 *   - staff: Entra / MSAL access token
 *   - external doctors / referrers / providers: app-session (email-OTP) JWT
 *
 * The previous `msalInstance.acquireTokenSilent` path only worked for MSAL
 * users: external OTP users have no MSAL account, so `getActiveAccount()`
 * returned null and the call threw ("No active account") — their uploads went
 * out unauthenticated and the backend returned `403 "Not authenticated"`, which
 * the UI showed as "Transcription service is unavailable".
 *
 * `refreshTokenIfNeeded()` keeps the MSAL token fresh; it is a no-op for
 * external app-session tokens (no MSAL account) and never logs the user out on
 * its own — so a best-effort refresh here is safe for both types.
 */
export async function getRequestAuthToken(): Promise<string> {
  try {
    await refreshTokenIfNeeded();
  } catch {
    // Best-effort refresh — fall through to whatever token we currently hold.
  }
  const token = getAccessTokenCookie();
  if (!token) {
    throw new Error("No active session, user needs to sign in");
  }
  return token;
}
